import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteFooter } from "@/components/SiteNav";
import { ContactSection } from "@/components/ContactSection";
import { serviceSchema, faqSchema } from "@/lib/service-schema";
import { ArrowRight, BookOpen, CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/gambling-tax-deductions")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://mytaxfella.com/gambling-tax-deductions" },
      { title: "IRS Gambling Losses & Casino Tax Deductions | MyTaxFella" },
      {
        name: "description",
        content:
          "How IRS gambling loss rules and casino gambling tax deductions actually work in 2026: the 90% loss limit, records the IRS expects, and when a tax form never arrives.",
      },
      { property: "og:title", content: "IRS Gambling Losses & Casino Tax Deductions | MyTaxFella" }, { property: "og:image", content: "https://mytaxfella.com/og-share-2026.jpg" }, { name: "twitter:image", content: "https://mytaxfella.com/og-share-2026.jpg" },
      {
        property: "og:description",
        content:
          "How IRS gambling loss rules and casino gambling tax deductions actually work in 2026: the 90% loss limit, records the IRS expects, and when a tax form never arrives.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "IRS Gambling Losses & Casino Tax Deductions | MyTaxFella" },
      {
        name: "twitter:description",
        content:
          "The 2026 rules for deducting gambling losses, reporting casino winnings, and the records the IRS expects.",
      },
    ],
    links: [{ rel: "canonical", href: "https://mytaxfella.com/gambling-tax-deductions" }],
    scripts: [
      serviceSchema({
        name: "Gambling Winnings and Losses Tax Help",
        description:
          "Tax preparation and planning for casino, sports betting, and other gambling winnings and losses, including the 2026 gambling loss deduction rules.",
        path: "/gambling-tax-deductions",
        serviceType: "Tax services for gambling winnings and losses",
      }),
      faqSchema(faqs),
    ],
  }),
  component: GamblingTaxDeductions,
});

const articles = [
  {
    slug: "casino-winnings-taxes-reporting",
    title: "Casino Winnings and Taxes: What You Owe, Even Without a Tax Form",
    description:
      "Casino winnings are taxable even when no W-2G shows up. What the reporting thresholds actually mean, and why under-$600 winnings still count.",
  },
  {
    slug: "sports-betting-taxes-without-withdrawing",
    title: "Sports Betting Winnings Still in Your App? What to Know About Taxes",
    description:
      "Winnings sitting in a betting account are still income. How receipt versus withdrawal works, and the records to keep before tax season.",
  },
  {
    slug: "gambling-losses-2026-deduction-records",
    title: "Gambling Losses in 2026: Records, Deductions, and Common Mistakes",
    description:
      "The 2026 rules cap deductible gambling losses at 90% of losses, and never more than winnings. What to document and what a losing year does not erase.",
  },
];

const keyPoints = [
  "All gambling winnings are taxable income, whether or not you receive a W-2G or any other tax form.",
  "The $600 and $1,200 thresholds you hear about are information-reporting thresholds for the payer, not tax-free allowances for you.",
  "For 2026, deductible gambling losses are limited to 90% of your losses, and total deductions can never exceed your winnings.",
  "Losses are an itemized deduction. If you take the standard deduction, your losses generally do not reduce your tax.",
  "A net losing year does not remove the obligation to report your winnings as income.",
  "The IRS expects contemporaneous records: a log or diary of sessions, plus receipts, statements, and wagering tickets.",
];

const faqs = [
  {
    question: "Can I deduct casino gambling losses on my taxes?",
    answer:
      "Yes, but only if you itemize deductions, and only up to the amount of gambling winnings you report as income. For tax year 2026, the deduction is further limited to 90% of your losses. You cannot use losses to offset other income like wages.",
  },
  {
    question: "Do I owe tax on casino winnings under $600?",
    answer:
      "Yes. The $600 figure is a threshold for when the payer may have to report the win to the IRS, not a tax-free allowance. All gambling winnings are taxable income and must be reported on your return, even small amounts with no tax form.",
  },
  {
    question: "What records does the IRS expect for gambling losses?",
    answer:
      "The IRS expects a diary or log of your gambling sessions with dates, locations, amounts won and lost, plus supporting documents like wagering tickets, receipts, casino statements, and betting app histories. Estimates made after the fact are a common audit problem.",
  },
  {
    question: "What changed about gambling loss deductions in 2026?",
    answer:
      "Starting with tax year 2026, deductible gambling losses are capped at 90% of your losses, and the deduction still cannot exceed your reported winnings. This means some gamblers with a break-even or losing year can still owe tax on part of their winnings.",
  },
  {
    question: "Are sports betting winnings taxed if I never withdraw them?",
    answer:
      "Yes. Winnings are generally taxable when they are credited to your account and available to you, not when you withdraw them. Money sitting in a betting app balance is still income for the year it was won.",
  },
];

function GamblingTaxDeductions() {
  return (
    <>
      <main className="pt-[72px] md:pt-20">
        <Hero />
        <KeyPoints />
        <JournalLinks />
        <Faq />
        <ContactSection />
      </main>
      <SiteFooter />
    </>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-border pt-16 pb-20 lg:pt-24 lg:pb-28">
      <div className="absolute inset-0 -z-10" style={{ background: "var(--gradient-hero)" }} />
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
          Gambling Income & Losses
        </p>
        <h1 className="mt-4 max-w-4xl font-display text-[clamp(2.25rem,5vw,4rem)] leading-[1.05] tracking-tight text-ink">
          IRS Gambling Losses and Casino Tax Deductions, Explained Plainly
        </h1>
        <p className="mt-6 max-w-2xl text-base lg:text-lg font-semibold leading-snug text-ink">
          Casino nights, sports betting apps, lottery tickets: the IRS treats all of it as taxable
          income, and the deduction rules for losses changed in 2026.
        </p>
        <p className="mt-3 max-w-2xl text-base lg:text-lg leading-relaxed text-muted-foreground">
          Here is what actually matters, and where to go deeper. We help gamblers nationwide, with
          offices in NY, NJ, and NC.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link to="/book" className="btn-green">
            Book an Appointment
          </Link>
          <a href="#journal-guides" className="btn-outline">
            Read the Guides
          </a>
        </div>
      </div>
    </section>
  );
}

function KeyPoints() {
  return (
    <section className="border-b border-border section-y">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <h2 className="section-h2">
          The Rules That Trip People Up
        </h2>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {keyPoints.map((point) => (
            <div
              key={point}
              className="pc-card"
            >
              <CheckCircle2 className="h-5 w-5 text-primary" />
              <p className="mt-4 text-sm leading-relaxed text-foreground">{point}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          This page is general information, not tax advice for your situation. The right answer
          depends on your records, your state, and the rest of your return.
        </p>
      </div>
    </section>
  );
}

function JournalLinks() {
  return (
    <section id="journal-guides" className="border-b border-border section-y">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex items-center gap-3">
          <BookOpen className="h-5 w-5 text-primary" />
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
            From the Journal
          </p>
        </div>
        <h2 className="mt-4 font-display text-3xl tracking-tight text-ink lg:text-4xl">
          Our In-Depth Gambling Tax Guides
        </h2>
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {articles.map((a) => (
            <Link
              key={a.slug}
              to="/blog/$slug"
              params={{ slug: a.slug }}
              className="group flex flex-col pc-card transition-colors hover:border-primary/50"
            >
              <h3 className="font-display text-xl leading-snug text-ink group-hover:text-primary">
                {a.title}
              </h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                {a.description}
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary">
                Read the guide
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section className="border-b border-border section-y">
      <div className="mx-auto max-w-[900px] px-6 lg:px-10">
        <h2 className="section-h2">
          Common Questions About Gambling Taxes
        </h2>
        <div className="mt-10 space-y-4">
          {faqs.map((f) => (
            <details
              key={f.question}
              className="pc-card group"
            >
              <summary className="cursor-pointer list-none font-medium text-foreground">
                {f.question}
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.answer}</p>
            </details>
          ))}
        </div>
        <p className="mt-10 text-sm leading-relaxed text-muted-foreground">
          Have a gambling tax question of your own? Call us at 718-356-5178 or book an appointment.
          We work with clients in all 50 states.
        </p>
        <div className="mt-8 flex flex-wrap gap-4 text-sm">
          <Link to="/individuals" className="font-medium text-primary hover:underline">
            Individual tax services
          </Link>
          <Link to="/tax-resolution" className="font-medium text-primary hover:underline">
            IRS letters and back taxes
          </Link>
          <Link to="/book" className="font-medium text-primary hover:underline">
            Book an appointment
          </Link>
        </div>
      </div>
    </section>
  );
}
